import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {OBJECTS_CREATABLE_PERMISSION} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import {BulkAssemblyBanner} from 'molbio/bulk-assembly/Banners';
import {PreviousTool} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {handleNewCloningBulkAssemblyClick} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonSize} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import runModal from 'ui-library/runModal';
import ToggleGroup from 'ui-library/ToggleGroup';
import {ElementMouseEvent} from 'util/Types';

import {Main, Nav, NavItem, Page, Pane} from '../BulkWorkflowComponents';
import * as BulkAssemblyActions from './BulkAssemblyActions';
import {
  BULK_ASSEMBLY_FINALIZE_IDX,
  BULK_ASSEMBLY_STATE,
  LocalAssembly,
  LOCKED_ASSEMBLY_STATES,
} from './BulkAssemblyConstants';
import BulkAssemblyStore from './BulkAssemblyStore';
import * as BulkAssemblyUtil from './BulkAssemblyUtil';
import AssemblyRunner from './components/AssemblyRunner';
import AssemblySummary from './components/AssemblySummary';
import GibsonParametersModal from './components/GibsonParametersModal';
import SlotEditor from './components/SlotEditor';
import SlotSummary from './components/SlotSummary';

interface BulkAssemblyEditorEnhancedProps {
  assembly: LocalAssembly;
}

class BulkAssemblyEditorConnector extends DataConnector<{}, BulkAssemblyEditorEnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [BulkAssemblyStore];

  enhanceProps(): BulkAssemblyEditorEnhancedProps {
    return {
      assembly: this.bulkAssemblyStore().getAssembly(),
    };
  }
}

export class BulkAssemblyEditor extends React.Component<BulkAssemblyEditorEnhancedProps> {
  componentDidMount(): void {
    BulkAssemblyActions.reset();
    analytics.deprecatedTrackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY);
  }

  _handleSetTopology(topology: SequenceTopology): void {
    BulkAssemblyActions.setTopology(topology);
  }

  _handleResetClick(): void {
    BulkAssemblyActions.reset();
  }

  _handleAddClick(): void {
    BulkAssemblyActions.addSlot();
  }

  _handleFinalizeClick(): void {
    BulkAssemblyActions.activateStep(BULK_ASSEMBLY_FINALIZE_IDX);
  }

  _handleSlotClick(e: ElementMouseEvent, idx: number): void {
    BulkAssemblyActions.activateStep(idx);
  }

  getSummaryThrobber(): JSX.Element | null {
    if (this.props.assembly.meta.state === BULK_ASSEMBLY_STATE.VALIDATING) {
      return (
        <span className="u-spacing-left">
          <HorizontalThrobber />
        </span>
      );
    }
    return null;
  }

  getSummaryHeader(): JSX.Element {
    const assembly = this.props.assembly;
    return (
      <div>
        {`${BulkAssemblyUtil.getSlotName(assembly, assembly.meta.activeSlotIndex)} Summary`}
        {this.getSummaryThrobber()}
      </div>
    );
  }

  _handleDeleteClick(e: ElementMouseEvent, idx: number): void {
    BulkAssemblyActions.removeSlot(idx);
  }

  _handleTagSchemaChange(tagSchema: {id: string} | null): void {
    if (!tagSchema || (this.props.assembly.tagSchemaId && tagSchema.id !== this.props.assembly.tagSchemaId)) {
      BulkAssemblyActions.resetSchemaFields();
    }
    BulkAssemblyActions.setTagSchemaId(tagSchema ? tagSchema.id : null);
  }

  async _openGibsonParameters(assembly: LocalAssembly): Promise<void> {
    await runModal(GibsonParametersModal, {});
    BulkAssemblyActions.validate();
  }

  render(): JSX.Element {
    const {assembly} = this.props;
    const {activeSlotIndex} = assembly.meta;
    const activeSlot = assembly.slots[activeSlotIndex];
    const hasErrors = BulkAssemblyUtil.getErrors(assembly).length > 0;
    const isLocked = LOCKED_ASSEMBLY_STATES.includes(assembly.meta.state);

    return (
      <Page
        className={classNames({'u-half-opacity u-no-mouse-target': isLocked})}
        {...getTestProps({component: 'BulkAssemblyEditor', element: 'root'})}
      >
        <Nav
          header="Bulk Assemble DNA"
          headerOptions={
            <div>
              <div className={css(styles.headerOption)}>
                <ToggleGroup
                  options={[
                    {id: SequenceTopology.CIRCULAR, buttonProps: {text: 'Circular'}},
                    {id: SequenceTopology.LINEAR, buttonProps: {text: 'Linear'}},
                  ]}
                  selection={assembly.topology}
                  onChangeSelection={(topology) => {
                    this._handleSetTopology(topology);
                  }}
                  size={ButtonSize.SMALL}
                />
              </div>

              <div className={css(styles.headerOption)}>
                <FieldLabel label="Schema (optional)" isRequired={false} className={css(styles.schema)} />
                <div className={css(styles.tagSchemaSelector, styles.schema)}>
                  <TagSchemaSelector
                    allowNone={true}
                    oligoAwareFolderItemTypes={[ModelTypeConstants.SEQUENCE]}
                    selectedTagSchemaId={assembly.tagSchemaId}
                    requiredPermissions={[OBJECTS_CREATABLE_PERMISSION]}
                    onChange={(tagSchema: {id: string} | null) => {
                      this._handleTagSchemaChange(tagSchema);
                    }}
                  />
                </div>
              </div>
            </div>
          }
          banner={
            BenchlingConfig.MOLBIO_REDESIGNED_CLONING_FLOW ? (
              <div className={css(styles.headerBanner)}>
                <BulkAssemblyBanner
                  actionOrigin={ActionOrigin.MOLBIO_BULK_ASSEMBLY}
                  onClick={() => {
                    analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_REDESIGNED_BULK_ASSEMBLY_LINK, {
                      actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY,
                    });
                    handleNewCloningBulkAssemblyClick(
                      {previousTool: PreviousTool.LEGACY_BULK_ASSEMBLY},
                      ActionOrigin.MOLBIO_BULK_ASSEMBLY
                    );
                  }}
                />
              </div>
            ) : (
              <></>
            )
          }
        >
          {assembly.slots.map((slot, i) => {
            const isActive = activeSlotIndex === i;
            const name = BulkAssemblyUtil.getSlotName(assembly, i);
            const hasSlotErrors = BulkAssemblyUtil.getSlotErrors(assembly, i).length > 0;
            const isRemovable = i !== 0;
            return (
              <NavItem
                key={i}
                id={i}
                isActive={isActive}
                onClick={(e: ElementMouseEvent, idx: number) => {
                  this._handleSlotClick(e, idx);
                }}
                hasErrors={hasSlotErrors}
                onDelete={
                  isRemovable
                    ? (e: ElementMouseEvent, idx: number) => {
                        this._handleDeleteClick(e, idx);
                      }
                    : undefined
                }
              >
                {name}
              </NavItem>
            );
          })}
          <div
            className={classNames('bulkWorkflow-nav-item', css(styles.addSlotWrapper))}
            onClick={() => {
              this._handleAddClick();
            }}
          >
            <BenchlingIcon icon="search-filter-add" className={css(styles.addSlotPlus)} />
          </div>
          <NavItem
            isActive={activeSlotIndex === BULK_ASSEMBLY_FINALIZE_IDX}
            onClick={() => {
              this._handleFinalizeClick();
            }}
            hasErrors={hasErrors}
          >
            Finalize
          </NavItem>
        </Nav>
        {activeSlot ? (
          <Main>
            <Pane className="bulkWorkflow-pane--narrow">
              <SlotEditor
                assembly={assembly}
                slotIdx={activeSlotIndex}
                onOpenPrimerSettings={() => {
                  this._openGibsonParameters(assembly);
                }}
              />
            </Pane>
            <Pane header={this.getSummaryHeader()}>
              <SlotSummary
                assembly={assembly}
                slotIdx={activeSlotIndex}
                onOpenPrimerSettings={() => {
                  this._openGibsonParameters(assembly);
                }}
              />
            </Pane>
          </Main>
        ) : (
          <Main>
            <Pane className="bulkWorkflow-pane--narrow">
              <AssemblyRunner assembly={assembly} />
            </Pane>
            <Pane header={<div>Assembly Summary{this.getSummaryThrobber()}</div>}>
              <AssemblySummary
                assembly={assembly}
                onOpenPrimerSettings={() => {
                  this._openGibsonParameters(assembly);
                }}
              />
            </Pane>
          </Main>
        )}
      </Page>
    );
  }
}

const styles = StyleSheet.create({
  addSlotWrapper: {
    '-webkit-border-radius': '40px',
    '-moz-border-radius': '40px',
    'border-radius': '40px',
    'background-color': Colors.WHITE,
    border: `1px dashed ${Colors.GRAY}`,
    'font-size': '32px',
    height: '80px',
    width: '80px',
    ':hover': {
      border: `1px solid ${Colors.BLUE_LIGHT}`,
    },
    ':hover span': {
      color: Colors.BLUE_LIGHT,
    },
  },
  addSlotPlus: {
    margin: '6px 14px',
    color: Colors.GRAY,
  },
  headerOption: {
    display: 'inline-block',
    verticalAlign: 'top',
    paddingRight: '15px',
  },
  headerBanner: {
    marginBottom: '16px',
  },
  schema: {
    display: 'inline-block',
    paddingRight: '15px',
  },
  tagSchemaSelector: {
    width: '250px',
  },
});

export default BulkAssemblyEditorConnector.connect(BulkAssemblyEditor);
